import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@auth/services';
import { environment } from '@env/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ConfirmDialogComponent } from '@shared/components';
import { StaticMessage } from '@shared/enums';
import { SelectedAccountService } from '@shared/services';
import { UserDetailsFacade } from '@shared/state/facades';
import { Constants, getStaticMessage } from '@shared/utils';

@UntilDestroy()
@Injectable()
export class ErrorHttpInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private userDetailsFacade: UserDetailsFacade,
    private selectedAccountService: SelectedAccountService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap({
        error: (error: any) => {
          if (error instanceof HttpErrorResponse) {
            switch (error.status) {
              case 401: {
                // Unauthorized
                this.handleUnauthorizedError();
                break;
              }
              case 403: {
                // Forbidden
                this.handleForbiddenError(request.method, request.url);
                break;
              }
              case 500: {
                // Internal Server Error
                this.handleInternalServerError(request.method, request.url);
                break;
              }
            }
          }
        },
      })
    );
  }

  private handleUnauthorizedError(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.errorInterceptor.titleUnauthorized:Unauthorized`,
        message: $localize`:@@shared.errorInterceptor.txtUnauthorized:You're not authorized to perform this action. After pressing OK you will be redirected to the login screen.`,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        tap(async () => {
          await this.authService.signoutRedirect();
        })
      )
      .subscribe();
  }

  private handleForbiddenError(method: string, url: string): void {
    // api sync from offline request errors are already handled separately in the main controller
    if (
      method === 'POST' &&
      url.indexOf(`${environment.dprApi.endpoint}/api/`) === 0 &&
      url.includes('/synchronize/offlinedata')
    ) {
      return;
    }

    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.errorInterceptor.titleForbidden:Forbidden`,
        message: $localize`:@@shared.errorInterceptor.txtForbidden:You don't have permissions for this page/operation. After pressing OK your permissions will be updated and you will be redirected to the first allowed page. Unsaved changes will be lost`,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.userDetailsFacade.query.selectedClientId$),
        switchMap(([_, selectedClientId]) =>
          this.selectedAccountService.selectClient(selectedClientId, true)
        )
      )
      .subscribe();
  }

  private handleInternalServerError(method: string, url: string): void {
    // api sync from offline request errors are already handled separately in the main controller
    if (
      method === 'POST' &&
      url.indexOf(`${environment.dprApi.endpoint}/api/`) === 0 &&
      url.includes('/synchronize/')
    ) {
      return;
    }

    this.dialog.open(ConfirmDialogComponent, {
      ...Constants.DialogOptions,
      data: {
        title: $localize`:@@shared.errorInterceptor.titleInternalServerError:Oops... something went wrong`,
        message: $localize`:@@shared.errorInterceptor.txtInternalServerError:Please try again, and if the error persists refresh the page!`,
        buttonLabel: getStaticMessage(StaticMessage.OkButton),
        hideCancelButton: true,
      },
    });
  }
}
